import React, { useEffect, useRef, useState } from 'react';
import { AxiosError } from 'axios';
import LoadingOverlay from 'helpers/LoadingOverlay';
import { processAPIError, filterUncommonErrors, translate } from 'utils';
import { renderErrors } from 'app/Components/common/Error';
import { shouldShowMoreShow, ShowMore } from 'app/Components/common/ShowMore';
import { TError } from 'models';
import { DrawerBodyStyled, DrawerCloseIcon, DrawerDataForm, DrawerHeaderRow, DrawerHeaderRowTitle } from 'app/Components/common/Drawer/styles';
import { renderControls, scrollElementToBottom } from 'app/Components/common/Drawer';
import { TBrokerGroup } from 'app/Pages/Broker/profile/body/Structure';
import { ContactPersonRow, ContactsItem } from 'styles/common';
import { Input } from '@insly/qmt-reactjs-ui-lib';

const TITLE = translate({ key: 'master.broker_group.branch.add' });

const BROKER_GROUP_TEMPLATE = {
  id: '',
  name: '',
  title: '',
  parent_id: ''
};

interface IProps {
  handleDrawerClose: () => void,
  handlePostBrokerGroup: (brokerGroup: TBrokerGroup, onError: (error: AxiosError) => void, onSuccess: () => void) => void,
  parentId: string,
}

//@TODO: Update Drawer Lib component and make children not required and remove unnecessary <div />
const DrawerAddBrokerGroup = ({ handleDrawerClose, handlePostBrokerGroup, parentId }: IProps) => (
  <>
    {renderHeaderRow(TITLE, handleDrawerClose)}
    <DrawerBody
      handlePostBrokerGroup={handlePostBrokerGroup}
      parentId={parentId}
    />
  </>
);

const DrawerBody = ({
  handlePostBrokerGroup,
  parentId
}: {
  handlePostBrokerGroup: IProps['handlePostBrokerGroup'],
  parentId: string,
}) => {
  const bodyEl = useRef<HTMLDivElement>(null);
  const [showMore, updateShowMore] = useState(false);

  useEffect(() => {
    if (bodyEl.current !== null) {
      shouldShowMoreShow(bodyEl.current, showMore, updateShowMore);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onBodyScroll = () => {
    if (bodyEl.current !== null) {
      shouldShowMoreShow(bodyEl.current, showMore, updateShowMore);
    }
  };

  return (
    <DrawerBodyStyled ref={bodyEl} onScroll={onBodyScroll} showMore={true}>
      <BrokerGroupForm
        handlePostBrokerGroup={handlePostBrokerGroup}
        parentId={parentId}
      />
      <ShowMore
        show={showMore}
        onClick={() => scrollElementToBottom(bodyEl.current)}
      />
    </DrawerBodyStyled>
  );
};

const renderHeaderRow = (title: string, handleDrawerClose: () => void) => (
  <DrawerHeaderRow>
    <DrawerHeaderRowTitle>{title}</DrawerHeaderRowTitle>
    <DrawerCloseIcon icon="close" onClick={handleDrawerClose} />
  </DrawerHeaderRow>
);

const BrokerGroupForm = ({
  handlePostBrokerGroup,
  parentId
}: {
  handlePostBrokerGroup: IProps['handlePostBrokerGroup'],
  parentId: string,
}) => {

  const [form, updateForm] = useState({
    ...BROKER_GROUP_TEMPLATE,
  });

  const [errors, updateErrors] = useState<TError[]>([]);
  const [isTouched, updateIsTouched] = useState(false);

  const [isSubmitting, updateIsSubmitting] = useState(false);

  const handleChange = (key?: string, value?: string) => {
    const touchedForm = { ...form };
    // @ts-ignore
    touchedForm[key] = value;

    updateErrors([]);
    updateIsTouched(true);
    updateForm(touchedForm);
  };

  const handleSaveData = () => {
    updateIsSubmitting(true);

    const postBrokerGroup = {
      parent_id: parentId,
      name: form.name,
      title: form.title,
    };

    handlePostBrokerGroup(postBrokerGroup, (error) => {
      updateErrors(() => processAPIError(error, true, 'masterService') as TError[]);
      updateIsSubmitting(false);
    }, () => {
      updateIsTouched(false);
      updateIsSubmitting(false);
    });
    updateIsTouched(false);
  };

  return (
    <DrawerDataForm>
      {isSubmitting ? <LoadingOverlay /> : null}
      {renderBrokerGroupForm(form, handleChange)}
      {errors?.length ? renderErrors(filterUncommonErrors(errors)) : null}
      {(isTouched) ? renderControls('list', isTouched, handleSaveData) : null}
    </DrawerDataForm>
  );

};

const renderBrokerGroupForm = (form: TBrokerGroup, handleChange: (key?: string, value?: string) => void) => (
  <ContactsItem>
    <ContactPersonRow gridTemplate="1fr">
      <Input
        label={translate({ key: 'master.brokers.branches_tag' })}
        name="name"
        value={form.name}
        handleChange={handleChange}
      />
      <Input
        label={translate({ key: 'master.common.title' })}
        name="title"
        value={form.title}
        handleChange={handleChange}
      />
    </ContactPersonRow>
  </ContactsItem>
);

export default DrawerAddBrokerGroup;
